import React, { useState } from 'react';

// Predefined source email accounts for email digest
// This list matches ALLOWED_SOURCE_EMAILS in the backend
const PREDEFINED_SOURCE_EMAILS = [
  { value: 'creators@culturegenesis.com', label: 'Creator Relations' },
  { value: 'adops@culturegenesis.com', label: 'Ad Operations' },
  { value: 'sales@culturegenesis.com', label: 'Sales' },
];

const DAYS_BACK_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days (recommended)' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

export interface EmailDigestConfigData {
  sourceEmail: string;
  recipients: string[];
  daysBack: number;
  includeAttachments: boolean;
  customSubject?: string;
}

interface EmailDigestConfigProps {
  config: EmailDigestConfigData;
  onChange: (config: EmailDigestConfigData) => void;
}

const EmailDigestConfig: React.FC<EmailDigestConfigProps> = ({ config, onChange }) => {
  const [recipientInput, setRecipientInput] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [showCustomSource, setShowCustomSource] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
      padding: '1rem',
      backgroundColor: 'var(--cg-neutral-25)',
      borderRadius: '8px',
      border: '1px solid var(--border-1)',
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      marginBottom: '0.25rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: 'var(--fg-1)',
    },
    select: {
      padding: '0.625rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      fontSize: '0.875rem',
      backgroundColor: 'var(--bg-surface-1)',
      color: 'var(--fg-1)',
    },
    input: {
      padding: '0.625rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      fontSize: '0.875rem',
      backgroundColor: 'var(--bg-surface-1)',
      color: 'var(--fg-1)',
    },
    recipientsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    recipientTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: 'var(--accent)',
      color: 'white',
      borderRadius: '4px',
      fontSize: '0.75rem',
    },
    removeButton: {
      background: 'none',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      padding: '0',
      fontSize: '1rem',
      lineHeight: 1,
    },
    addRecipientRow: {
      display: 'flex',
      gap: '0.5rem',
    },
    addButton: {
      padding: '0.625rem 1rem',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: 'var(--accent)',
      color: 'white',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    errorText: {
      color: 'var(--danger)',
      fontSize: '0.75rem',
      marginTop: '0.25rem',
    },
    helpText: {
      color: 'var(--fg-2)',
      fontSize: '0.75rem',
      marginTop: '0.25rem',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    checkbox: {
      width: '16px',
      height: '16px',
    },
  };

  const handleSourceEmailChange = (value: string) => {
    if (value === 'custom') {
      setShowCustomSource(true);
    } else {
      setShowCustomSource(false);
      onChange({ ...config, sourceEmail: value });
    }
  };

  const handleCustomEmailChange = (value: string) => {
    setCustomEmail(value);
    // Validate it's a culturegenesis.com email
    if (value.toLowerCase().endsWith('@culturegenesis.com')) {
      onChange({ ...config, sourceEmail: value.toLowerCase() });
    }
  };

  const validateRecipient = (email: string): string | null => {
    const emailLower = email.toLowerCase().trim();
    if (!emailLower.includes('@')) {
      return 'Invalid email format';
    }
    const domain = emailLower.split('@')[1];
    if (domain !== 'culturegenesis.com' && domain !== 'culturegenesis-partnerships.com') {
      return 'Recipients must be @culturegenesis.com or @culturegenesis-partnerships.com';
    }
    if (config.recipients.includes(emailLower)) {
      return 'Recipient already added';
    }
    return null;
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (!email) return;

    const error = validateRecipient(email);
    if (error) {
      setRecipientError(error);
      return;
    }

    setRecipientError(null);
    onChange({
      ...config,
      recipients: [...config.recipients, email.toLowerCase()],
    });
    setRecipientInput('');
  };

  const removeRecipient = (email: string) => {
    onChange({
      ...config,
      recipients: config.recipients.filter((r) => r !== email),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRecipient();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.sectionTitle}>Email Digest Settings</div>

      {/* Source Email */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Source Email Account</label>
        <select
          style={styles.select}
          value={showCustomSource ? 'custom' : config.sourceEmail}
          onChange={(e) => handleSourceEmailChange(e.target.value)}
        >
          {PREDEFINED_SOURCE_EMAILS.map((email) => (
            <option key={email.value} value={email.value}>
              {email.label} ({email.value})
            </option>
          ))}
          <option value="custom">Other...</option>
        </select>
        {showCustomSource && (
          <input
            style={styles.input}
            type="email"
            placeholder="email@culturegenesis.com"
            value={customEmail}
            onChange={(e) => handleCustomEmailChange(e.target.value)}
          />
        )}
        <p style={styles.helpText}>
          The mailbox to read emails from. Must have domain-wide delegation configured.
        </p>
      </div>

      {/* Custom Email Subject (optional) */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Email Subject (optional)</label>
        <input
          style={styles.input}
          type="text"
          placeholder="Email Digest: sales@culturegenesis.com (Feb 11 - Feb 18, 2026)"
          value={config.customSubject || ''}
          onChange={(e) => onChange({ ...config, customSubject: e.target.value || undefined })}
          maxLength={200}
        />
        <p style={styles.helpText}>
          Leave blank to use the default format: "Email Digest: {'{source}'} ({'{date range}'})"
        </p>
      </div>

      {/* Recipients */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Send Digest To</label>
        <div style={styles.addRecipientRow}>
          <input
            style={{ ...styles.input, flex: 1 }}
            type="email"
            placeholder="team@culturegenesis.com"
            value={recipientInput}
            onChange={(e) => {
              setRecipientInput(e.target.value);
              setRecipientError(null);
            }}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            style={styles.addButton}
            onClick={addRecipient}
          >
            Add
          </button>
        </div>
        {recipientError && <p style={styles.errorText}>{recipientError}</p>}
        {config.recipients.length === 0 && (
          <p style={styles.helpText}>
            Add at least one @culturegenesis.com recipient to receive the digest.
          </p>
        )}
        <div style={styles.recipientsList}>
          {config.recipients.map((email) => (
            <span key={email} style={styles.recipientTag}>
              {email}
              <button
                type="button"
                style={styles.removeButton}
                onClick={() => removeRecipient(email)}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Days Back */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Include emails from last</label>
        <select
          style={styles.select}
          value={config.daysBack}
          onChange={(e) => onChange({ ...config, daysBack: Number(e.target.value) })}
        >
          {DAYS_BACK_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Include Attachments */}
      <div style={styles.formGroup}>
        <div style={styles.checkboxRow}>
          <input
            type="checkbox"
            id="includeAttachments"
            style={styles.checkbox}
            checked={config.includeAttachments}
            onChange={(e) =>
              onChange({ ...config, includeAttachments: e.target.checked })
            }
          />
          <label htmlFor="includeAttachments" style={styles.label}>
            Include attachment information in digest
          </label>
        </div>
        <p style={styles.helpText}>
          When enabled, the digest will note which emails have attachments.
        </p>
      </div>
    </div>
  );
};

export default EmailDigestConfig;
